import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';

/**
 * One component, one colour map, used everywhere — docs/04 §4.
 *
 * The label is always rendered as text. Status is never conveyed by colour alone
 * (docs/04 §6), which also means this stays legible to anyone who cannot separate the
 * violet "approved" from the blue "submitted".
 */
const VARIANTS = {
  draft: 'neutral',
  submitted: 'info',
  approved: 'violet',
  posted: 'success',
  partially_paid: 'warning',
  paid: 'success-solid',
  overdue: 'destructive',
  void: 'muted',
  cancelled: 'muted',
  reversed: 'outline-destructive',

  // Period and generic lifecycle states share the map so a badge never falls back to
  // "default" and quietly loses its meaning.
  open: 'success',
  locked: 'warning',
  closed: 'neutral',
  active: 'success',
  inactive: 'neutral',
  pending: 'info',
  completed: 'success',
  rejected: 'destructive',

  // Commission lifecycle (Phase 7). `accrued` is informational — the machine has computed
  // it and nothing has reached the ledger; `payable` shares `approved`'s colour because
  // that is exactly what it is, plus a release to be paid.
  accrued: 'info',
  payable: 'violet',
};

export const statusVariant = (status) => VARIANTS[status] || 'neutral';

export const StatusBadge = ({ status, label, className }) => {
  const { t } = useTranslation('common');
  if (!status) return <span className="text-muted-foreground">—</span>;

  return (
    <Badge variant={statusVariant(status)} className={className}>
      {label || t(`status.${status}`, { defaultValue: status })}
    </Badge>
  );
};

export default StatusBadge;
